import * as vp from 'vopay';
import * as Yup from 'yup';
import { ChangeEvent, FocusEvent, ReactNode } from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import { useTranslation } from 'react-i18next';
import SelectOptionLocked from '../../../../UI/Select/SelectOptionLocked';
import { ElinxFieldId, ElinxFormFields } from '../types';
import { getElinxConnectionMethods, getRequiredIcon, isElinxActive, notificationUsesPhone } from '../utils';
import styles from '../AccountingSystemOpen.module.scss';

const {
  Box,
  ElementContainer,
  MultiSelectChip,
  Radio,
  TextField,
  CardCheckbox,
  Tooltip,
  Title,
  InteracWordmark,
  withStyles,
} = vp;

const cn = withStyles(styles);

export type ElinxFormValues = ElinxFormFields & {
  PaymentMethod?: string;
  PaymentDate?: string;
  PaymentAmount?: string;
  CreditAmount?: number;
};

export type ElinxRenderBag = {
  values: ElinxFormValues;
  errors: FormikErrors<ElinxFormValues>;
  touched: FormikTouched<ElinxFormValues>;
  handleChange: (event: ChangeEvent<HTMLElement>) => void;
  handleBlur: (event: FocusEvent<HTMLElement>) => void;
  setFieldValue: (field: string, value: string | boolean, shouldValidate?: boolean) => void | Promise<unknown>;
};

export type ElinxRow = {
  id?: ElinxFieldId;
  type?: 'elinx';
  container: 'field';
  require: (ctx: ElinxFormValues) => boolean;
  visible: (ctx: ElinxFormValues) => boolean;
  validate?: (ctx: ElinxFormValues) => unknown;
  render?: (bag: ElinxRenderBag) => ReactNode;
  paymentRows: never[];
};

type ElinxMethodOption = {
  label: string;
  value: string;
  featureKey: string;
  visible: boolean;
  renderLabel?: ReactNode;
};

export type UseElinxRowsProps = {
  type: 'Payable' | 'Receivable';
  country: string;
  iQ11Enabled: boolean;
};

const splitValues = (value?: string): string[] => (value || '').split(',').filter(Boolean);

const removeValue = (value: string | undefined, toRemove: string): string =>
  splitValues(value)
    .filter((v) => v !== toRemove)
    .join(',');

const selectedMethods = (ctx: ElinxFormValues): string[] => splitValues(ctx.ElinxPaymentSelectionMethod);
const hasBankMethod = (ctx: ElinxFormValues): boolean => selectedMethods(ctx).includes('bank');

const hasCardMethod = (ctx: ElinxFormValues): boolean =>
  selectedMethods(ctx).includes('credit') || selectedMethods(ctx).includes('debitcard');

export default function useElinxRows({ type, country, iQ11Enabled }: UseElinxRowsProps): ElinxRow[] {
  const { t: tElinx } = useTranslation('translations', { keyPrefix: 'AccountingSystem.OpenTab.Elinx' });
  const { t: tElinxPage } = useTranslation('translations', { keyPrefix: 'Elinx.Form' });
  const isPay = type === 'Payable';
  // Only notification + email/phone are supported for now; flip to show the other eLinx fields.
  const isElinxFieldsSupported: boolean = false;

  const paymentMethodTypeOptions: ElinxMethodOption[] = [
    {
      label: tElinx('paymentTypeBank', { defaultValue: 'Bank Account' }),
      value: 'bank',
      featureKey: 'Bank Account',
      visible: true,
    },
    {
      label: isPay ? 'Interac e-Transfer®' : 'Interac e-Transfer® Request Money',
      renderLabel: <InteracWordmark type={isPay ? 'eTransfer' : 'moneyRequest'} />,
      value: 'email',
      featureKey: isPay ? 'Interac e-Transfer' : 'Interac e-Transfer Money Request',
      visible: country === 'CA',
    },
    {
      label: tElinx('paymentTypeCredit', { defaultValue: 'Credit Card' }),
      value: 'credit',
      featureKey: 'Credit Card',
      visible: !isPay,
    },
    {
      label: tElinx('paymentTypeDebit', { defaultValue: 'Debit Card' }),
      value: 'debitcard',
      featureKey: 'Debit Card',
      visible: isPay,
    },
    {
      label: tElinx('paymentTypeGooglePay', { defaultValue: 'Google Pay' }),
      value: 'googlepay',
      featureKey: 'Google Pay',
      visible: !isPay,
    },
    {
      label: tElinx('paymentTypePaypal', { defaultValue: 'PayPal' }),
      value: 'paypal',
      featureKey: 'PayPal',
      visible: isPay,
    },
    {
      label: tElinx('paymentTypeVenmo', { defaultValue: 'Venmo' }),
      value: 'venmo',
      featureKey: 'Venmo',
      visible: isPay && country === 'US',
    },
    {
      label: tElinx('paymentTypeZelle', { defaultValue: 'Zelle' }),
      value: 'zelle',
      featureKey: 'Zelle',
      visible: isPay && country === 'US',
    },
  ].filter((option) => option.visible);

  const railOptions: ElinxMethodOption[] = [
    {
      label: tElinx('railEft', { defaultValue: 'Electronic Funds Transfer' }),
      value: 'eft',
      featureKey: isPay ? 'EFTSendEnabled' : 'EFTCollectEnabled',
      visible: country === 'CA',
    },
    {
      label: tElinx('railVopayInstant', { defaultValue: 'VoPay Instant' }),
      value: 'vopayinstant',
      featureKey: 'RTRWithdrawEnabled',
      visible: country === 'CA' && isPay,
    },
    {
      label: tElinx('railAch', { defaultValue: 'ACH' }),
      value: 'ach',
      featureKey: country === 'US' && isPay ? 'ACHWithdrawEnabled' : 'ACHFundEnabled',
      visible: country === 'US',
    },
    {
      label: tElinx('railRtp', { defaultValue: 'RTP' }),
      value: 'rtp',
      featureKey: 'RTPWithdrawEnabled',
      visible: country === 'US' && isPay,
    },
    {
      label: tElinx('railWire', { defaultValue: 'Wire' }),
      value: 'fedwire',
      featureKey: 'WireEnabled',
      visible: country === 'US' && isPay,
    },
  ].filter((option) => option.visible);

  const connectionOptions = getElinxConnectionMethods(iQ11Enabled).map((method) => ({
    label: tElinx(`connectionMethod_${method}`, { defaultValue: method }),
    value: method,
  }));

  const cardBrandOptions = vp.allCardBrands.map((brand) => ({
    label: brand.charAt(0).toUpperCase() + brand.slice(1),
    value: brand,
  }));

  const lockedRenderOption = (option: ElinxMethodOption, args: vp.SelectRenderOptionArgs) => (
    <SelectOptionLocked {...args} featureKey={option.featureKey}>
      {option.renderLabel || option.label}
    </SelectOptionLocked>
  );

  const fieldLabel = (label: string, tooltip?: ReactNode) => (
    <Box jc="fs" ai="center" gapSize="4px">
      {label} {getRequiredIcon()}
      {tooltip}
    </Box>
  );

  const requiredSchema = (ctx: ElinxFormValues, when: boolean) =>
    isElinxActive(ctx) && when
      ? Yup.string().required(tElinx('required', { defaultValue: 'This value is required.' }))
      : Yup.string().notRequired();

  return [
    {
      id: 'ElinxPaymentSelectionMethod',
      type: 'elinx',
      container: 'field',
      require: () => false,
      visible: (ctx) => isElinxFieldsSupported && isElinxActive(ctx),
      validate: (ctx) => (isElinxFieldsSupported ? requiredSchema(ctx, true) : Yup.string().notRequired()),
      paymentRows: [],
      render: ({ values, errors, touched, handleBlur, setFieldValue }) => (
        <ElementContainer
          label={fieldLabel(
            tElinx('paymentMethodTypesLabel', { defaultValue: 'Request Payment Method Types' }),
            <Tooltip
              information={
                <Box gapSize="4px" flx="col">
                  <h3 className={cn('toolTipHeader')}>
                    {tElinxPage('ContactPaymentSettings.RequestPaymentMethodType.Tooltip.title', {
                      defaultValue: 'Payment Method Type',
                    })}
                  </h3>

                  <div>
                    {tElinxPage('ContactPaymentSettings.RequestPaymentMethodType.Tooltip.description', {
                      defaultValue:
                        'This lets you select the type of payment method that you would like to request from your contact.',
                    })}
                  </div>
                </Box>
              }
              position="top"
              width="394px"
            />,
          )}
          width="100%"
          justifyContent="flex-start"
          isBold={false}
          padding="0 0 6px"
          input={
            <MultiSelectChip
              name="ElinxPaymentSelectionMethod"
              placeholder={tElinx('paymentMethodTypesPlaceholder', { defaultValue: 'Select' })}
              options={paymentMethodTypeOptions}
              optionValue={(option) => option.value}
              value={splitValues(values.ElinxPaymentSelectionMethod)}
              onChange={(e) => setFieldValue('ElinxPaymentSelectionMethod', e.target.value)}
              onDelete={(deleted) =>
                setFieldValue('ElinxPaymentSelectionMethod', removeValue(values.ElinxPaymentSelectionMethod, deleted))
              }
              onBlur={handleBlur}
              renderOption={lockedRenderOption}
              sort={false}
              errorMsg={
                errors.ElinxPaymentSelectionMethod && touched.ElinxPaymentSelectionMethod
                  ? errors.ElinxPaymentSelectionMethod
                  : undefined
              }
            />
          }
        />
      ),
    },
    {
      id: 'ElinxBankingPaymentRail',
      type: 'elinx',
      container: 'field',
      require: () => false,
      visible: (ctx) => isElinxFieldsSupported && isElinxActive(ctx) && hasBankMethod(ctx),
      validate: (ctx) =>
        isElinxFieldsSupported ? requiredSchema(ctx, hasBankMethod(ctx)) : Yup.string().notRequired(),
      paymentRows: [],
      render: ({ values, errors, touched, handleBlur, setFieldValue }) => (
        <div className={cn('elinxSection')}>
          <Title
            size="xxsm"
            textSize="17px"
            bold
            title={tElinx('bankAccountTitle', { defaultValue: 'Bank Account' })}
          />

          <Box flx="col" gapSize="24px" width="100%">
            <ElementContainer
              label={fieldLabel(tElinx('paymentRailLabel', { defaultValue: 'Payment Rail' }))}
              width="100%"
              justifyContent="flex-start"
              isBold={false}
              padding="0 0 6px"
              input={
                <MultiSelectChip
                  name="ElinxBankingPaymentRail"
                  placeholder={tElinx('paymentRailPlaceholder', { defaultValue: 'Select' })}
                  options={railOptions}
                  optionValue={(option) => option.value}
                  value={splitValues(values.ElinxBankingPaymentRail)}
                  onChange={(e) => setFieldValue('ElinxBankingPaymentRail', e.target.value)}
                  onDelete={(deleted) =>
                    setFieldValue('ElinxBankingPaymentRail', removeValue(values.ElinxBankingPaymentRail, deleted))
                  }
                  onBlur={handleBlur}
                  renderOption={lockedRenderOption}
                  sort={false}
                  errorMsg={
                    errors.ElinxBankingPaymentRail && touched.ElinxBankingPaymentRail
                      ? errors.ElinxBankingPaymentRail
                      : undefined
                  }
                />
              }
            />

            <ElementContainer
              label={fieldLabel(tElinx('connectionMethodLabel', { defaultValue: 'Bank Account Connection Method' }))}
              width="100%"
              justifyContent="flex-start"
              isBold={false}
              padding="0 0 6px"
              input={
                <MultiSelectChip
                  name="ElinxConnectionMethods"
                  placeholder={tElinx('connectionMethodPlaceholder', { defaultValue: 'Select' })}
                  options={connectionOptions}
                  optionValue={(option) => option.value}
                  value={splitValues(values.ElinxConnectionMethods)}
                  onChange={(e) => setFieldValue('ElinxConnectionMethods', e.target.value)}
                  onDelete={(deleted) =>
                    setFieldValue('ElinxConnectionMethods', removeValue(values.ElinxConnectionMethods, deleted))
                  }
                  onBlur={handleBlur}
                  sort={false}
                  errorMsg={
                    errors.ElinxConnectionMethods && touched.ElinxConnectionMethods
                      ? errors.ElinxConnectionMethods
                      : undefined
                  }
                />
              }
            />
          </Box>
        </div>
      ),
    },
    {
      id: 'ElinxConnectionMethods',
      type: 'elinx',
      container: 'field',
      require: () => false,
      visible: () => false,
      validate: (ctx) =>
        isElinxFieldsSupported ? requiredSchema(ctx, hasBankMethod(ctx)) : Yup.string().notRequired(),
      paymentRows: [],
    },
    {
      id: 'ElinxAcceptedCardBrands',
      type: 'elinx',
      container: 'field',
      require: () => false,
      visible: (ctx) => isElinxFieldsSupported && isElinxActive(ctx) && hasCardMethod(ctx),
      validate: (ctx) =>
        isElinxFieldsSupported ? requiredSchema(ctx, hasCardMethod(ctx)) : Yup.string().notRequired(),
      paymentRows: [],
      render: ({ values, errors, touched, handleBlur, setFieldValue }) => (
        <div className={cn('elinxSection')}>
          <Title
            size="xxsm"
            textSize="17px"
            bold
            title={tElinx('cardSettingsTitle', { defaultValue: 'Card Settings' })}
          />

          <Box flx="col" gapSize="24px" width="100%">
            <ElementContainer
              label={fieldLabel(
                tElinx('acceptedCardBrandsLabel', { defaultValue: 'Accepted Card Brands' }),
                <Tooltip
                  information={tElinxPage('CardSettings.acceptedCardBrands.tooltip', {
                    defaultValue: 'Select the card brands this contact should be able to provide.',
                  })}
                  position="top"
                  width="394px"
                />,
              )}
              width="100%"
              justifyContent="flex-start"
              isBold={false}
              padding="0 0 6px"
              input={
                <MultiSelectChip
                  name="ElinxAcceptedCardBrands"
                  placeholder={tElinx('acceptedCardBrandsPlaceholder', { defaultValue: 'Select' })}
                  options={cardBrandOptions}
                  optionValue={(option) => option.value}
                  value={splitValues(values.ElinxAcceptedCardBrands)}
                  onChange={(e) => setFieldValue('ElinxAcceptedCardBrands', e.target.value)}
                  onDelete={(deleted) =>
                    setFieldValue('ElinxAcceptedCardBrands', removeValue(values.ElinxAcceptedCardBrands, deleted))
                  }
                  onBlur={handleBlur}
                  errorMsg={
                    errors.ElinxAcceptedCardBrands && touched.ElinxAcceptedCardBrands
                      ? errors.ElinxAcceptedCardBrands
                      : undefined
                  }
                />
              }
            />

            <div className={cn('elinxCardTypeRow')}>
              <Box jc="fs" ai="center" gapSize="4px">
                {tElinx('cardTypeValidationLabel', { defaultValue: 'Card Type Validation' })}

                <Tooltip
                  information={tElinxPage('CardSettings.cardTypeValidation.tooltip', {
                    defaultValue:
                      "Validates whether the entered card is debit or credit and blocks cards that don't match the allowed types.",
                  })}
                  position="top"
                  width="394px"
                />
              </Box>

              <CardCheckbox
                name="ElinxCardTypeValidation"
                className={cn('elinxCardCheckbox')}
                label={tElinx('cardTypeValidationCheckbox', { defaultValue: 'Enable card type validation' })}
                checked={!!values.ElinxCardTypeValidation}
                onChange={() => setFieldValue('ElinxCardTypeValidation', !values.ElinxCardTypeValidation)}
              />
            </div>
          </Box>
        </div>
      ),
    },
    {
      id: 'ElinxNotificationVia',
      type: 'elinx',
      container: 'field',
      require: (ctx) => isElinxActive(ctx),
      visible: (ctx) => isElinxActive(ctx),
      validate: (ctx) => requiredSchema(ctx, true),
      paymentRows: [],
      render: ({ values, errors, touched, setFieldValue }) => (
        <ElementContainer
          label={fieldLabel(tElinx('notificationLabel', { defaultValue: 'Send eLinx Notification via' }))}
          width="100%"
          justifyContent="flex-start"
          isBold={false}
          padding="0 0 6px"
          input={
            <Radio
              variant="outlineShadow"
              name="ElinxNotificationVia"
              value={String(values.ElinxNotificationVia || '')}
              noOrdering
              options={{
                email: tElinx('notificationEmail', { defaultValue: 'Email' }),
                sms: tElinx('notificationSms', { defaultValue: 'SMS' }),
              }}
              onChange={(e) => setFieldValue('ElinxNotificationVia', e.target.value)}
              error={errors.ElinxNotificationVia && touched.ElinxNotificationVia ? errors.ElinxNotificationVia : null}
            />
          }
        />
      ),
    },
    {
      id: 'ElinxCustomerEmail',
      type: 'elinx',
      container: 'field',
      require: (ctx) => isElinxActive(ctx) && ctx.ElinxNotificationVia === 'email',
      visible: (ctx) => isElinxActive(ctx) && ctx.ElinxNotificationVia === 'email',
      validate: (ctx) =>
        isElinxActive(ctx) && ctx.ElinxNotificationVia === 'email'
          ? Yup.string()
              .email(tElinx('invalidEmail', { defaultValue: 'Invalid email address.' }))
              .required(tElinx('required', { defaultValue: 'This value is required.' }))
          : Yup.string().notRequired(),
      paymentRows: [],
      render: ({ values, errors, touched, handleChange, handleBlur }) => (
        <ElementContainer
          label={fieldLabel(tElinx('customerEmailLabel', { defaultValue: 'Customer Email' }))}
          width="100%"
          justifyContent="flex-start"
          isBold={false}
          padding="0 0 6px"
          input={
            <TextField
              variant="outlineShadow"
              preset="email"
              name="ElinxCustomerEmail"
              placeholder=""
              onBlur={handleBlur}
              onChange={handleChange}
              value={String(values.ElinxCustomerEmail || '')}
              error={errors.ElinxCustomerEmail && touched.ElinxCustomerEmail ? errors.ElinxCustomerEmail : null}
            />
          }
        />
      ),
    },
    {
      id: 'ElinxCustomerPhone',
      type: 'elinx',
      container: 'field',
      require: (ctx) => isElinxActive(ctx) && notificationUsesPhone(ctx.ElinxNotificationVia),
      visible: (ctx) => isElinxActive(ctx) && notificationUsesPhone(ctx.ElinxNotificationVia),
      validate: (ctx) => requiredSchema(ctx, notificationUsesPhone(ctx.ElinxNotificationVia)),
      paymentRows: [],
      render: ({ values, errors, touched, handleChange, handleBlur }) => (
        <ElementContainer
          label={fieldLabel(tElinx('customerPhoneLabel', { defaultValue: 'Customer Phone Number' }))}
          width="100%"
          justifyContent="flex-start"
          isBold={false}
          padding="0 0 6px"
          input={
            <TextField
              variant="outlineShadow"
              preset="tel"
              name="ElinxCustomerPhone"
              placeholder=""
              onBlur={handleBlur}
              onChange={handleChange}
              value={String(values.ElinxCustomerPhone || '')}
              error={errors.ElinxCustomerPhone && touched.ElinxCustomerPhone ? errors.ElinxCustomerPhone : null}
            />
          }
        />
      ),
    },
  ];
}
